import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ToastContainer, type ToastType, type ToastProps } from '@/components/ui/Toast';

interface ToastOptions {
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  toast: {
    success: (options: ToastOptions | string) => void;
    error: (options: ToastOptions | string) => void;
    warning: (options: ToastOptions | string) => void;
    info: (options: ToastOptions | string) => void;
  };
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

let toastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, options: ToastOptions | string) => {
      const id = `toast-${++toastId}`;
      const normalizedOptions: ToastOptions =
        typeof options === 'string' ? { title: options } : options;

      const newToast: ToastProps = {
        id,
        type,
        title: normalizedOptions.title,
        message: normalizedOptions.message,
        duration: normalizedOptions.duration ?? 5000,
        onClose: removeToast,
      };

      setToasts((prev) => [...prev, newToast]);
    },
    [removeToast]
  );

  const toast = {
    success: (options: ToastOptions | string) => addToast('success', options),
    error: (options: ToastOptions | string) => addToast('error', options),
    warning: (options: ToastOptions | string) => addToast('warning', options),
    info: (options: ToastOptions | string) => addToast('info', options),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
}
